import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useContext, useState } from "react";
import { Alert, Typography } from "@mui/material";
import { ArrowBack, Close, Error } from "@mui/icons-material";
import { ProfileResponse } from "@/utils/Hooks";
import { ProfileData } from "@/types/shared.types";
import { KeyedMutator } from "swr";
import MessageContext from '@/context/MessageProvider';

const BorrowModalPages = [
  {
    title: "Akhir",
    modal: ReviewPage
  },
  {
    title: "Selesai",
    modal: SelesaiPage,
    noReturn: true,
    excludeOnStages: true
  }
];

type BorrowModalProps = {
  accessToken: string,
  setOpen: (open: boolean) => void,
  profile?: {
    data: ProfileData | undefined;
    mutate: KeyedMutator<ProfileResponse> | undefined
  } | undefined

  koleksi: any,
  handleRootBack: () => void,
}

export default function BorrowModal({ accessToken, setOpen, profile, koleksi, handleRootBack }: BorrowModalProps) {
  const [page, setPage] = useState(0);
  const handleClose = () => setOpen(false);

  const goToNextPage = () => {
    if (page === BorrowModalPages.length - 1) {
      handleClose();
      return;
    }
    setPage(page + 1);
  };

  const goToPrevPage = () => {
    if (page === 0) {
      handleRootBack();
      return;
    }
    setPage(page - 1);
  };

  return <>
    <Box sx={
      {
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        marginBottom: 1
      }
    }>
      {BorrowModalPages.map(({ title, modal, excludeOnStages }, index) => {
        if (excludeOnStages) {
          return null;
        }
        return <Box
          sx={{ flexGrow: 1 }}
          key={index}
        >
          <Box
            sx={{
              borderBottom: "2px solid",
              borderBottomColor: index <= page ? "primary.main" : "rgba(0, 0, 0, 0.20)"
            }}
          ></Box>
          <Typography>{title}</Typography>
        </Box>;
      })}
      <Button
        sx={{
          flexShrink: 1,
          minWidth: 0,
          padding: 0
        }}
        onClick={handleClose}>
        <Close/>
      </Button>
    </Box>

    {(!BorrowModalPages[page].noReturn) && <Button
      variant="outlined"
      onClick={goToPrevPage}
      size="small"
      startIcon={<ArrowBack/>}
      sx={{
        marginBottom: 1
      }}
    >
      Kembali
    </Button>
    }

    {!profile?.data?.kunjungan && <Alert severity="error" sx={{
      marginBottom: 2
    }}>Sepertinya Anda mencoba meminjam di luar kunjungan. Jika Anda sudah di Perpustakaan STIS, slakukan pendaftaran
      kunjungan terlebih dahulu.</Alert>}

    {BorrowModalPages.map(({ title, modal }, index) => {
      if (index !== page) {
        return null;
      }
      const Page = modal;
      return <Page
        key={title}
        accessToken={accessToken}
        koleksi={koleksi}
        profile={profile}
        nextCallback={goToNextPage}
        prevCallback={goToPrevPage}
      />;
    })}
  </>;
}

export type KoleksiRaw = {
  id: string;
  noInduk: string;
  id_jenis_pustaka: string;
  id_klasifikasi: number;
  qrcode: string;
  judul: string;
  penulis: string;
  tahun_terbit: string;
  kolasi: string;
  kode_rak: string;
  tanggal_masuk: string;
  info_tambahan: string;
  createdAt: string;
  updatedAt: string;
}

type BorrowModalPageProps = {
  key?: any;

  nextCallback: () => void;
  prevCallback: () => void;
  accessToken: string;
  profile?: {
    data: ProfileData | undefined;
    mutate: KeyedMutator<ProfileResponse> | undefined
  }

  koleksi: KoleksiRaw;
}

function ReviewPage(
  {
    nextCallback,
    prevCallback,
    accessToken,
    koleksi,
    profile,
    key
  }: BorrowModalPageProps) {
  const [loading, setLoading] = useState(false);
  const { setMessage } = useContext(MessageContext)!;

  const submitVisitor = async () => {
    setLoading(true);

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/peminjaman`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        koleksiId: koleksi.id
      })
    });

    setLoading(false);

    const responseJson = await response.json();

    if (!response.ok) {
      setMessage({ isOpen: true, content: responseJson.message ?? "Terjadi sebuah kesalahan. Silahkan coba lagi nanti", severity: "error" });
      return;
    }

    nextCallback();

  };

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Review kembali</Typography>

    <Typography>
      Anda akan meminjam buku berikut.
    </Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Judul</Typography>
      <Typography>{koleksi.judul}</Typography>
    </Box>

    {koleksi.penulis && <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Penulis</Typography>
      <Typography>{koleksi.penulis}</Typography>
    </Box>}

    {koleksi.noInduk && <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>No. Induk</Typography>
      <Typography>{koleksi.noInduk}</Typography>
    </Box>}

    {koleksi.qrcode && <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Kode QR</Typography>
      <Typography>{koleksi.qrcode}</Typography>
    </Box>}

    <Typography>
      Dengan melakukan peminjaman di Perpustakaan STIS, Anda setuju untuk mengikuti tata tertib dan aturan yang berlaku.
    </Typography>

    <Button
      variant="contained"
      onClick={submitVisitor}
      disabled={!profile?.data?.kunjungan || loading}
      sx={{
        width: '100%'
      }}
      size="large"
    >
      Kirim
    </Button>
  </Box>;
}

function SelesaiPage({ nextCallback, key }: BorrowModalPageProps) {
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      alignItems: 'center'
    }}>
      <Error sx={{
        width: 96,
        height: 96
      }}/>

      <Typography component="h2" sx={{
        fontSize: 20,
        fontWeight: 700
      }}>Peminjaman hampir selesai</Typography>
    </Box>
    <Typography>
      Satu langkah lagi! <b>Segera konfirmasi peminjaman Anda dengan petugas.</b>
    </Typography>
    <Typography>
      Peminjaman Anda telah diajukan, namun Anda perlu menghubungi petugas perpustakaan untuk menyelesaikan peminjaman
      Anda.
    </Typography>

    <Button
      variant="contained"
      onClick={nextCallback}
      sx={{
        width: '100%'
      }}
      size="large"
    >
      Tutup
    </Button>

    {/* TODO Buat jeda sebelum tombol Tutup dapat diklik */}
  </Box>;
}